import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../api/client'
import { API_ENDPOINTS } from '../api/endpoints'
import {
  DROPDOWN_FIELD_CONFIG_KEY,
  type DropdownFieldConfigMap,
  type DropdownTypeFieldConfig,
  type DropdownTypeKey,
  getDefaultDropdownFieldConfig,
  getDropdownTypeDefinition,
  parseDropdownFieldConfig,
} from '../utils/dropdownFieldConfig'

export function useDropdownFieldConfigMap() {
  const query = useQuery({
    queryKey: ['systemSettings', DROPDOWN_FIELD_CONFIG_KEY],
    queryFn: async () => {
      try {
        const response = await apiClient.get(API_ENDPOINTS.systemSettings.get(DROPDOWN_FIELD_CONFIG_KEY))
        const raw = response.data?.settingValue ?? response.data?.SettingValue ?? response.data
        return parseDropdownFieldConfig(raw)
      } catch (error: any) {
        if (error?.response?.status === 404) {
          return getDefaultDropdownFieldConfig()
        }
        throw error
      }
    },
    staleTime: 5 * 60 * 1000,
  })

  const config = query.data ?? getDefaultDropdownFieldConfig()

  return {
    config,
    isLoading: query.isLoading,
    refetch: query.refetch,
  }
}

export function useDropdownFieldConfig(type: DropdownTypeKey): DropdownTypeFieldConfig {
  const { config } = useDropdownFieldConfigMap()
  return useMemo(() => {
    const def = getDropdownTypeDefinition(type)
    return config[type] ?? def.defaults
  }, [config, type])
}

export type { DropdownFieldConfigMap, DropdownTypeFieldConfig, DropdownTypeKey }
